import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { HotkeyContext, type HotkeyScope } from './HotkeyContext';

function normalizeCombo(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push('Ctrl');
  if (e.shiftKey) parts.push('Shift');
  if (e.altKey) parts.push('Alt');
  parts.push(e.key);
  return parts.join('+');
}

/**
 * Горячие клавиши окон/списков (1С-стиль — Insert/F2/Esc, без пересечений с Chrome, см.
 * CLAUDE.md). Один слушатель `keydown` в bubble-фазе на `document`: срабатывает только если
 * событие ещё не остановлено раньше (например, `LinesGrid` гасит Esc во время редактирования
 * ячейки через `stopPropagation` в capture-фазе — до этого слушателя оно не доходит). Стек
 * скоупов — от последнего зарегистрированного (самое «верхнее» окно) к первому.
 */
export function HotkeyProvider({ children }: { children: ReactNode }) {
  const scopesRef = useRef<HotkeyScope[]>([]);

  const push = useCallback((scope: HotkeyScope) => {
    scopesRef.current = [...scopesRef.current, scope];
  }, []);

  const remove = useCallback((id: string) => {
    scopesRef.current = scopesRef.current.filter((s) => s.id !== id);
  }, []);

  const touch = useCallback((id: string) => {
    const scope = scopesRef.current.find((s) => s.id === id);
    if (!scope) return;
    scopesRef.current = [...scopesRef.current.filter((s) => s.id !== id), scope];
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // antd `Modal` (PreviewConsequencesDialog, UnsavedChangesDialog и т.п.) держит фокус внутри
      // себя, пока открыт, и сам обрабатывает Escape/Enter — если событие пришло оттуда, окну под
      // ним (FloatingWindow) наши горячие клавиши срабатывать не должны (иначе Escape закрыл бы
      // сразу и диалог, и документ под ним).
      if (e.target instanceof Element && e.target.closest('.ant-modal, .ant-modal-wrap')) return;
      const combo = normalizeCombo(e);
      for (let i = scopesRef.current.length - 1; i >= 0; i -= 1) {
        const binding = scopesRef.current[i]!.bindingsRef.current.find((b) => b.combo === combo);
        if (binding) {
          e.preventDefault();
          binding.handler();
          return;
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return <HotkeyContext.Provider value={{ push, remove, touch }}>{children}</HotkeyContext.Provider>;
}
